import { useTranslation } from 'react-i18next';
import { Modal } from '~/components/overlays/Modal';
import { ModalHeader } from '~/components/overlays/ModalHeader';
import { Button } from '~/components/ui';
import { BuyListTallyKind, type SkippedOrderItem } from '~/graphql/graphql';

function useSkippedItemLabel() {
  const { t } = useTranslation('order');
  return (item: SkippedOrderItem) => {
    switch (item.tallyKind) {
      case BuyListTallyKind.Canned:
        return t('order.copy_to_cart_skipped_canned_tally', {
          productName: item.productName,
          tallyName: item.tallyName,
          tallyCount: item.tallyCount,
        });
      case BuyListTallyKind.Random:
        return t('order.copy_to_cart_skipped_random_tally', {
          productName: item.productName,
        });
      default:
        return item.productName;
    }
  };
}

export function CopySkippedItemsModal({
  skippedItems,
  onOkay,
  title,
}: Readonly<{
  skippedItems: SkippedOrderItem[] | null;
  onOkay: () => void;
  title?: string;
}>) {
  const { t } = useTranslation('order');
  const skippedItemLabel = useSkippedItemLabel();
  const isOpen = skippedItems !== null;

  return (
    <Modal isOpen={isOpen} onClose={onOkay}>
      <div
        className="bg-neut-bg-alt rounded-lg w-full max-w-md"
        data-testid="copy-skipped-items-modal"
      >
        <ModalHeader
          title={title ?? t('order.copy_to_cart_skipped_title')}
          onClose={onOkay}
        />
        <div className="p-6">
          {skippedItems != null && skippedItems.length > 0 ? (
            <ul className="space-y-2 mb-4">
              {skippedItems.map((item, index) => (
                <li
                  key={`${item.productName}-${index}`}
                  className="text-sm text-neut-sec"
                >
                  <span className="font-medium text-neut-prim">
                    {skippedItemLabel(item)}
                  </span>
                  {' — '}
                  {t(`order.copy_to_cart_skip_reason_${item.reason}`)}
                </li>
              ))}
            </ul>
          ) : (
            <p className="text-sm text-neut-sec mb-4">
              {t('order.copy_to_cart_skipped_none_available')}
            </p>
          )}
          <Button onClick={onOkay} data-testid="copy-skipped-items-okay">
            {t('order.copy_to_cart_skipped_okay')}
          </Button>
        </div>
      </div>
    </Modal>
  );
}
